/**
 * `/groups` — the groups.
 *
 * A group is the unit that makes access survive people. Granting a folder to
 * "Editors" and adding somebody to Editors is two acts that each stay true;
 * granting it to four named people is four acts that go stale the moment
 * somebody joins.
 */
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AsyncContent } from '../../components/AsyncContent';
import { TablePager } from '../../components/TablePager';
import { EmptyState } from '../../components/EmptyState';
import { FormDialog } from '../../components/FormDialog';
import { PageHeader } from '../../components/PageHeader';
import { useAsync } from '../../hooks/useAsync';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubmit } from '../../hooks/useSubmit';
import { useAuth } from '../../auth/useAuth';
import { createGroup, listGroups } from '../../api/identity';
import { BLANK_GROUP_FORM, useGroupFields } from '../entities/groupActions';

export default function GroupsPage() {
  const { t } = useTranslation('people');
  const { can } = useAuth();
  // Cards rather than a table, so there are no column headers to click —
  // but the endpoint is paged, and without a pager the second page of groups
  // would simply be unreachable. Sorted by name, which is the only order a
  // card grid reads in.
  const list = useListQuery({ defaultSort: 'name' });
  const groups = useAsync(() => listGroups(list.query), [list.key]);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(BLANK_GROUP_FORM);
  // The same fields the rename dialog on `GroupDetailPage` renders.
  const groupFields = useGroupFields();

  const create = useSubmit(async () => {
    const created = await createGroup({
      name: form.name.trim(),
      ...(form.description.trim() ? { description: form.description.trim() } : {}),
    });
    setForm(BLANK_GROUP_FORM);
    groups.reload();
    return created;
  });

  return (
    <>
      <PageHeader
        title={t('groups.title')}
        description={t('groups.description')}
        actions={
          can('group.create') && (
            <Button
              variant="contained"
              startIcon={<GroupAddIcon />}
              onClick={() => setCreating(true)}
            >
              {t('groups.new')}
            </Button>
          )
        }
      />

      <AsyncContent state={groups}>
        {({ groups: rows, page }) =>
          rows.length === 0 ? (
            <EmptyState
              icon={<GroupsIcon fontSize="inherit" />}
              title={t('groups.emptyTitle')}
              description={t('groups.emptyDescription')}
            />
          ) : (
            <>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              }}
            >
              {rows.map((group) => (
                <Card key={group.id} variant="outlined">
                  <CardActionArea
                    component={RouterLink}
                    to={`/groups/${group.id}`}
                    sx={{ p: 2.5, alignItems: 'flex-start' }}
                  >
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                      <GroupsIcon sx={{ color: 'primary.main' }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                          {group.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {group.description ?? t('groups.noDescription')}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardActionArea>
                </Card>
              ))}
            </Box>
            <TablePager
              page={page}
              current={list.page}
              onPageChange={list.setPage}
              onLimitChange={list.setLimit}
            />
            </>
          )
        }
      </AsyncContent>

      <FormDialog
        open={creating}
        title={t('groups.createDialog.title')}
        submitLabel={t('groups.createDialog.submit')}
        pending={create.pending}
        error={create.error}
        disabled={form.name.trim() === ''}
        onClose={() => setCreating(false)}
        onSubmit={async () => {
          if (await create.submit()) setCreating(false);
        }}
      >
        {groupFields(form, (changes) => setForm((current) => ({ ...current, ...changes })))}
      </FormDialog>
    </>
  );
}
